// src/Api/AnnouncementsApi.js
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../firebaseConfig";
import { authFetch } from "../Utils/authFetch";

const API_BASE_URL = process.env.REACT_APP_API_URL;

async function readJsonSafe(res) {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

function buildError(res, data, fallbackCode) {
  const code = data?.error || fallbackCode || "request_failed";
  const e = new Error(code);
  e.code = code;
  e.httpStatus = res?.status;
  return e;
}

/* =============================================================================
   LISTA (ETag conditional) — authFetch (token + 304 tuki)
============================================================================= */
export const fetchAnnouncements = async (companyId, { etag } = {}) => {
  if (!companyId) {
    const e = new Error("companyId_required");
    e.code = "companyId_required";
    throw e;
  }

  const headers = {};
  if (etag) headers["If-None-Match"] = etag;

  const url = `${API_BASE_URL}/fetchAnnouncements?companyId=${encodeURIComponent(
    companyId
  )}`;

  const res = await authFetch(url, { method: "GET", headers });

  if (res.status === 304) {
    return { status: 304 };
  }

  const body = await readJsonSafe(res);
  if (!res.ok) {
    throw buildError(res, body, "fetch_announcements_failed");
  }

  const newEtag = res.headers.get("ETag");

  return {
    status: 200,
    announcements: body?.announcements || [],
    version: body?.version || 0,
    updatedAt: body?.updatedAt || null,
    etag: newEtag,
  };
};

/* =============================================================================
   DETAIL — authFetch
============================================================================= */
export const fetchAnnouncementById = async (companyId, announcementId) => {
  if (!companyId || !announcementId) {
    const e = new Error("missing_fields");
    e.code = "missing_fields";
    throw e;
  }

  const url = `${API_BASE_URL}/fetchAnnouncementById?companyId=${encodeURIComponent(
    companyId
  )}&announcementId=${encodeURIComponent(announcementId)}`;

  const res = await authFetch(url, { method: "GET" });
  const body = await readJsonSafe(res);

  if (!res.ok) {
    throw buildError(res, body, "fetch_announcement_failed");
  }
  return body; // announcement + comments + attachments
};

/* =============================================================================
   CREATE — authFetch
   HUOM: backend ottaa createdBy uid:n tokenista, joten createdBy voi olla null
============================================================================= */
export const createAnnouncement = async ({
  companyId,
  title,
  content,
  category,
  audience,
  createdBy, // (legacy) sallittu, mutta backend preferoi token-uid
}) => {
  const res = await authFetch(`${API_BASE_URL}/createAnnouncement`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      companyId,
      title,
      content,
      category,
      audience,
      createdBy,
    }),
  });

  const body = await readJsonSafe(res);
  if (!res.ok) {
    throw buildError(res, body, "create_announcement_failed");
  }
  return body; // { id }
};

/* =============================================================================
   UPDATE — authFetch
============================================================================= */
export const updateAnnouncement = async (companyId, announcementId, patch) => {
  const res = await authFetch(`${API_BASE_URL}/updateAnnouncement`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ companyId, announcementId, patch }),
  });

  const body = await readJsonSafe(res);
  if (!res.ok) {
    throw buildError(res, body, "update_announcement_failed");
  }
  return body; // { ok:true }
};

/* =============================================================================
   COMMENT — authFetch
   HUOM: backend ottaa senderId uid:n tokenista; senderId payloadissa voi olla null
============================================================================= */
export const addAnnouncementComment = async (
  companyId,
  announcementId,
  payload = {}
) => {
  const { body, senderId = null, senderName = "", parentId = null } = payload || {};

  const trimmed = typeof body === "string" ? body.trim() : "";
  if (!companyId || !announcementId || !trimmed) {
    const e = new Error("missing_fields");
    e.code = "missing_fields";
    throw e;
  }

  const res = await authFetch(`${API_BASE_URL}/addAnnouncementComment`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      companyId,
      announcementId,
      body: trimmed,
      senderId, // legacy
      senderName,
      parentId,
    }),
  });

  const data = await readJsonSafe(res);
  if (!res.ok) {
    throw buildError(res, data, "add_comment_failed");
  }

  return {
    id: data?.id,
    body: data?.body || trimmed,
    senderId: data?.senderId ?? senderId ?? null,
    senderName: data?.senderName ?? senderName ?? "",
    parentId: data?.parentId ?? parentId ?? null,
    likes: data?.likes ?? 0,
    likedBy: Array.isArray(data?.likedBy) ? data.likedBy : [],
    createdAt: typeof data?.createdAt === "number" ? data.createdAt : Date.now(),
  };
};

/* =============================================================================
   ATTACHMENTS — client -> Storage, metadata -> backend (authFetch)
============================================================================= */
export const uploadAnnouncementAttachment = async (companyId, announcementId, file) => {
  if (!companyId || !announcementId || !file) {
    const e = new Error("missing_fields");
    e.code = "missing_fields";
    throw e;
  }

  const safeName = `${Date.now()}_${file.name}`.replace(/\s+/g, "_");
  const storagePath = `companies/${companyId}/announcements/${announcementId}/attachments/${safeName}`;

  // 1) upload Firebase Storageen (SDK hoitaa authin)
  const fileRef = ref(storage, storagePath);
  await uploadBytes(fileRef, file, { contentType: file.type });
  const url = await getDownloadURL(fileRef);

  // 2) rekisteröi metadata backendissä (authFetch → token mukaan)
  const res = await authFetch(`${API_BASE_URL}/registerAnnouncementAttachment`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      companyId,
      announcementId,
      name: file.name,
      size: file.size,
      url,
      storagePath,
      contentType: file.type || null,
    }),
  });

  const body = await readJsonSafe(res);
  if (!res.ok) {
    throw buildError(res, body, "register_attachment_failed");
  }
  return body; // attachment-objekti
};

export const deleteAnnouncementAttachment = async (
  companyId,
  announcementId,
  attachmentId,
  storagePath
) => {
  const res = await authFetch(`${API_BASE_URL}/deleteAnnouncementAttachment`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      companyId,
      announcementId,
      attachmentId,
      storagePath, // voi olla undefined; backend hakee myös Firestoresta jos puuttuu
    }),
  });

  const body = await readJsonSafe(res);
  if (!res.ok) {
    throw buildError(res, body, "delete_attachment_failed");
  }
  return body; // { ok:true }
};

/* =============================================================================
   VIEWED — authFetch
   HUOM: backend käyttää token-uid:tä ensisijaisesti
============================================================================= */
export const markAnnouncementAsViewed = async (companyId, announcementId, userId) => {
  const res = await authFetch(`${API_BASE_URL}/markAnnouncementAsViewed`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ companyId, announcementId, userId }), // userId legacy
  });

  const body = await readJsonSafe(res);
  if (!res.ok) {
    throw buildError(res, body, "mark_view_failed");
  }
  return body; // { ok:true, viewedByCount }
};

/* =============================================================================
   DELETE — authFetch
============================================================================= */
export const deleteAnnouncement = async (companyId, announcementId) => {
  const res = await authFetch(`${API_BASE_URL}/deleteAnnouncement`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ companyId, announcementId }),
  });

  const body = await readJsonSafe(res);
  if (!res.ok) {
    throw buildError(res, body, "delete_failed");
  }
  return body; // { ok:true }
};

/* =============================================================================
   LIKE (toggle) — authFetch
   HUOM: backend käyttää token-uid:tä ensisijaisesti
============================================================================= */
export const likeAnnouncementComment = async ({
  companyId,
  announcementId,
  commentId,
  userId, // legacy
  userName,
}) => {
  const res = await authFetch(`${API_BASE_URL}/likeAnnouncementComment`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      companyId,
      announcementId,
      commentId,
      userId, // legacy
      userName,
    }),
  });

  const data = await readJsonSafe(res);
  if (!res.ok) {
    throw buildError(res, data, "like_comment_failed");
  }

  return {
    likedBy: Array.isArray(data?.likedBy) ? data.likedBy : [],
    likes: data?.likes || 0,
  };
};

/* =============================================================================
   EXPORT
============================================================================= */
const AnnouncementsApi = {
  fetchAnnouncements,
  fetchAnnouncementById,
  createAnnouncement,
  updateAnnouncement,
  addAnnouncementComment,
  uploadAnnouncementAttachment,
  deleteAnnouncementAttachment,
  markAnnouncementAsViewed,
  deleteAnnouncement,
  likeAnnouncementComment,
};

export default AnnouncementsApi;
